import { getDeviceCode } from '../utils/deviceId'
import { toDayKey } from '../utils/dayKey'

const KEY = 'kermes.receiptSeq'

interface SeqState {
  dayKey: string
  seq: number
}

function read(): SeqState {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as SeqState
      if (typeof parsed.dayKey === 'string' && Number.isInteger(parsed.seq)) return parsed
    }
  } catch {
    // Corrupt entry, fall through and start the day over.
  }
  return { dayKey: toDayKey(), seq: 0 }
}

function write(state: SeqState): void {
  localStorage.setItem(KEY, JSON.stringify(state))
}

/**
 * Issues the next receipt number for this device, e.g. "K1-0042".
 *
 * A server-side counter cannot work here: offline, increment() is applied as a
 * local estimate, so two disconnected tills would both produce the same value
 * and collide on sync. Global sequential numbering is fundamentally
 * incompatible with multi-device offline. Prefixing a per-device local counter
 * is collision-free by construction and needs no coordination.
 *
 * The Firestore document id remains the real unique key, this is a display
 * label, and must never be used to look an order up.
 */
export function nextReceiptNo(): string {
  const today = toDayKey()
  const state = read()
  const seq = state.dayKey === today ? state.seq + 1 : 1
  write({ dayKey: today, seq })
  return `${getDeviceCode()}-${String(seq).padStart(4, '0')}`
}

export function peekReceiptSeq(): number {
  const state = read()
  return state.dayKey === toDayKey() ? state.seq : 0
}

/**
 * Recovers the counter after localStorage is cleared mid-service, by seeding it
 * from the highest sequence already issued by this device today. Orders come
 * from the local cache, so this works offline.
 */
export function seedReceiptSeq(todaysReceiptNos: string[]): void {
  const today = toDayKey()
  const state = read()
  const current = state.dayKey === today ? state.seq : 0
  const prefix = `${getDeviceCode()}-`

  let highest = current
  for (const no of todaysReceiptNos) {
    if (!no.startsWith(prefix)) continue
    const seq = Number(no.slice(prefix.length))
    if (Number.isInteger(seq) && seq > highest) highest = seq
  }

  if (highest !== current || state.dayKey !== today) {
    write({ dayKey: today, seq: highest })
  }
}
